import _ from "lodash";
import moment from "moment";
import { formatDateTime } from "../../../utils";
import getReferrerFullName from "../services/getReferrerFullName";
import getCaseManagerById from "../services/getCaseManagerById";
import { CREATION_TIME_SECTION } from "../constants";
import { generateCreationTimestamp, getGpReportCellDisplayText } from "../utils";
import { WORKFLOW_APPT_STATUS } from "../../../constants";

import momentTZ from "moment-timezone";
import { DEFAULT_TIMEZONE } from "../../../constants";
const generateCoordinatorOfCA = (ca, path, coordinators) =>
  _.find(coordinators, (coordinator) => {
    return _.get(ca, path, "") === coordinator.value;
  }) || { label: "", value: "" };

const generateBookFilterInfo = (ca, coordinators) => {
  const { label: statusLabel, value: statusValue } =
    _.find(WORKFLOW_APPT_STATUS, ({ value }) => value === ca.appointment_t?.status) || {};
  const { label, value } = generateCoordinatorOfCA(ca, "appointment_t.coordinator", coordinators);
  return [_.toLower(statusLabel), _.toLower(statusValue), _.toLower(label), _.toLower(value)];
};

export const bookingInfoFilter = (fieldValue, coordinators) => (clientAppointments) =>
  clientAppointments.filter((ca) => {
    const infoLists = generateBookFilterInfo(ca, coordinators);
    return infoLists.some((info) => _.includes(info, _.toLower(fieldValue)));
  });

export const coordinatorFilter = (fieldValue, path, coordinators) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const { label, value } = generateCoordinatorOfCA(ca, path, coordinators);
    return _.includes(_.toLower(label), _.toLower(fieldValue)) || _.includes(_.toLower(value), _.toLower(fieldValue));
  });
};

export const gpReportStatusFilter = (keyword) => (clientAppointments) => {
  const normalizedKeyword = keyword.toLowerCase();
  return clientAppointments.filter((ca) => getGpReportCellDisplayText(ca).toLowerCase().includes(normalizedKeyword));
};

export const generalFieldFilter = (fieldValue, path) => (clientAppointments) => {
  return clientAppointments.filter((ca) => _.includes(_.toLower(_.get(ca, path)), _.toLower(fieldValue)));
};

export const generalBooleanFilter = (fieldValue, path) => (clientAppointments) => {
  return clientAppointments.filter((ca) => _.get(ca, path, false));
};

export const usernameFieldFilter = (filterValue) => (clientAppointments) => {
  if (_.startsWith(filterValue, "-") && filterValue.length === 20) {
    return clientAppointments.filter((ca) => ca.id === filterValue);
  }
  return clientAppointments.filter((ca) => {
    const clientFullName = `${_.toString(_.get(ca, "client.firstName"))} ${_.toString(_.get(ca, "client.lastName"))}`;
    return _.includes(_.toLower(clientFullName), _.toLower(filterValue));
  });
};

export const dateFieldFilter = (filterValue, path) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const dateMoment = moment(_.get(ca, path, ""), "YYYY-MM-DD");
    if (dateMoment.isValid()) {
      return (
        dateMoment.format("DD/MM/YYYY").indexOf(filterValue) >= 0 ||
        dateMoment.format("YYYY-MM-DD").indexOf(filterValue) >= 0
      );
    }
    return false;
  });
};

export const creationDateTimeFieldFilter = (filterValue, type = CREATION_TIME_SECTION.CREATION_TIME) => (
  clientAppointments
) => {
  return clientAppointments.filter((ca) => {
    const time = generateCreationTimestamp(ca, type);
    const dateTimeWeek = momentTZ.unix(time).tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD HH:mm [W]WW");
    return dateTimeWeek.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
  });
};

export const appointmentDateFieldFilter = (filterValue) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const dateTimeWeek = formatDateTime(_.get(ca, "appointment.startDateTime", ""), true, true);
    return dateTimeWeek.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
  });
};

export const referrerFieldFilter = (filterValue, referrers) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const referrerFullName = getReferrerFullName(ca, referrers);
    return referrerFullName.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
  });
};

export const caseManagerFieldFilter = (filterValue, practitioners) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const caseManagerFullName = getCaseManagerById(ca, practitioners);
    return caseManagerFullName.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
  });
};

export const specialityFieldFilter = (filterValue, practitioners, practitionerProfiles) => (clientAppointments) => {
  return clientAppointments.filter((ca) => {
    const practitionerId = _.get(ca, "practitioner.practitionerId", "");
    const specialityName =
      (practitionerProfiles.find(({ id }) => practitionerId === id)?.specialties || [])[0]?.name || "";
    return specialityName.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
  });
};
